import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post, setCsrfToken, ApiError } from './client';

export type SessionState = 'active' | 'mfa_pending' | 'mfa_enrollment_required' | 'password_change_required';
export type Session = {
  state: SessionState;
  user: { id: number; email: string; full_name: string };
  roles: string[];
  permissions: string[];
  csrf_token: string;
  is_privileged: boolean;
  mfa_enrolled: boolean;
  stepup_fresh: boolean;
};

export const sessionKey = ['auth', 'session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: async () => {
      try {
        const s = await get<Session>('/auth/session');
        setCsrfToken(s.csrf_token);
        return s;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setCsrfToken(null);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api<Session>('/auth/login', { method: 'POST', body }),
    onSuccess: (s) => { setCsrfToken(s.csrf_token); qc.setQueryData(sessionKey, s); },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<void>('/auth/logout'),
    onSettled: () => { setCsrfToken(null); qc.setQueryData(sessionKey, null); qc.clear(); },
  });
}

export function useMfaVerify(path: '/auth/mfa/verify' | '/auth/mfa/stepup' = '/auth/mfa/verify') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => post<Session>(path, { code }),
    onSuccess: (s) => { setCsrfToken(s.csrf_token); qc.setQueryData(sessionKey, s); },
  });
}

export const hasPermission = (s: Session | null | undefined, perm: string) => !!s && s.permissions.includes(perm);
export const hasAnyPermission = (s: Session | null | undefined, perms: string[]) => !!s && perms.some((p) => s.permissions.includes(p));
